<template>
  <div id="document-tab">
    <table>
      <thead>
        <tr>
          <th>{{ $t('DOC_TYPE') }}</th>
          <th>{{ $t('UPLOADER') }}</th>
          <th>{{ $t('CREATED_DATE') }}</th>
          <th>{{ $t('ACTIONS') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr :key="doc.id" v-for="doc in docs">
          <td>{{ $t(getDocTypeKey(doc.typeId)) }}</td>
          <td>{{ doc.createdBy.firstName }} {{ doc.createdBy.lastName }}</td>
          <td>{{ moment(doc.createdAt).format('DD/MM/YYYY | HH:mm') }}</td>
          <td>
            <font-awesome-icon class="icon" @click="view(doc.path)" :icon="faEye" />
            <font-awesome-icon class="icon" @click="deleteDocument(doc.id)" :icon="faTrash" />
          </td>
        </tr>
      </tbody>
    </table>

    <div class="btns float-right">
      <primary-button class="button" @click="navigateReprint" :icon="faPrint" independence :text="$t('VIEW_CONTRACT')" />
      <primary-button class="button" @click="clickUpload" :icon="faCloudUploadAlt" blue :text="$t('UPLOAD')" />
    </div>

    <document-upload v-model="on" />
    <confirm-modal />
  </div>
</template>

<script>
import moment from 'moment';
import { ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import { faCloudUploadAlt, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PrimaryButton from '@/components/PrimaryButton';
import ConfirmModal from '@/components/ConfirmModal';

import ClientDetailServices from '@/views/ClientDetail/ClientDetailServices';
import DocumentUpload from './DocumentUpload';

const { useState, useActions } = createNamespacedHelpers('clientDetail');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useActions: useConfirmActions } = createNamespacedHelpers('confirm');
const { useActions: useModalActions } = createNamespacedHelpers('modal');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'DocumentTab',
  components: {
    PrimaryButton,
    FontAwesomeIcon,
    DocumentUpload,
    ConfirmModal
  },
  setup(_, { root }) {
    const { clientDocTypes } = useAppState(['clientDocTypes']);
    const { docs, targetingClient: client } = useState(['docs', 'targetingClient']);
    const { getClientDocs } = useActions(['getClientDocs']);
    const { info: flashInfo } = useFlashActions(['info', 'error']);
    const { setTitleModal, setOnConfirm } = useConfirmActions(['setTitleModal', 'setOnConfirm']);
    const { toggleConfirmModal: toggleConfirm } = useModalActions(['toggleConfirmModal']);

    const on = ref(false);

    const getDocTypeKey = (typeId) => {
      const docType = clientDocTypes.value.find((t) => t.id === typeId);
      return docType?.key || '';
    };

    const clickUpload = () => { on.value = !on.value; };

    const view = (link) => {
      window.open(link, '_blank');
    };

    const deleteDocument = (id) => {
      setTitleModal(root.$t('CONFIRM_DELETE_PAPER'));

      const onConfirm = async () => {
        await ClientDetailServices.deleteDocument(id);
        flashInfo('SUCCESSFULLY_DELETED_DOCUMENT');
        getClientDocs();
      };

      setOnConfirm(onConfirm);
      toggleConfirm();
    };

    watch(client, (newDetail) => {
      if (newDetail && newDetail.id) {
        getClientDocs();
      }
    }, {
      immediate: true
    });

    return {
      moment,
      faCloudUploadAlt,
      faEye,
      faTrash,
      docs,
      clickUpload,
      view,
      deleteDocument,
      on,
      getDocTypeKey
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#document-tab {
  table {
    width: 100%;
    border-collapse: inherit;
    border-spacing: 0;
    background-color: $ghostWhite;

    thead {
      text-transform: uppercase;
      font-weight: bold;
      text-align: center;

      tr {

        th {
          background-color: $brightMaroon;
          color: $white;
          padding: 15px 10px;
          border-top: 1px solid $alto;
          border-bottom: 1px solid $alto;
          font-size: 14px;

          &:first-child {
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-left: 1px solid $alto;

          }

          &:last-child {
            border-top-right-radius: 3px;
            border-bottom-right-radius: 3px;
            border-right: 1px solid $alto;
          }
        }
      }
    }

    tbody {
      text-align: center;

      tr {
        background-color: $babyPowder;
        transition: all .2s ease-in-out;

        &:hover {
          background-color: $ghostWhite;
        }

        td {
          padding: 15px 5px;
          border-top: 1px solid $alto;
          border-bottom: 1px solid $alto;

          &:first-child {
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-left: 1px solid $alto;
          }

          &:last-child {
            border-top-right-radius: 3px;
            border-bottom-right-radius: 3px;
            border-right: 1px solid $alto;
          }

          .icon {
            margin-right: 10px;
            cursor: pointer;
          }
        }
      }
    }
  }

  .btns {
    margin: 20px 0;
    padding-right: 15px;

    .primary-button {
      margin-left: 15px;
      border-radius: 4px;
    }
  }

  @media (max-width: 767px) {

    table {
      padding-top: 50px;
      width: auto;
      display: block;

      thead {
        display: block;

        tr {
          position: absolute;
          top: -9999px;
          left: -9999px;
        }
      }

      tbody {
        text-align: start;
        display: block;

        tr {
          display: block;

          &:last-child {
            border-bottom: 1px solid $alto;
          }

          td {
            padding: 2px;
            display:block;
            border: none;
            position: relative;
            padding-left: 45%;
            padding-right: 6px;
            min-height: 30px;

            &:first-child {
              border-left: unset;
              border-top: 1px solid $alto;
              padding-top: 20px;
              border-top-left-radius: unset;
            }

            &:last-child {
              border-right: unset;
              padding-bottom: 20px;
            }

            &::before {
              position: absolute;
              top: 0;
              left: 6px;
              width: 45%;
              padding-right: 10px;
              white-space: nowrap;
              text-align: center;
            }

            .mobile-header {
              position: absolute;
              left: 6px;
              padding-right: 10px;
              white-space: nowrap;
              text-align: center;
              padding-left: 10px;
            }
          }
        }
      }
    }
  }
}

</style>
